import { Container, Heading, Stack } from "@chakra-ui/react";
import { Footer, Navigation } from "components";
import { PersonDocument, PersonQuery, PersonSlugsDocument } from "generated";
import type { GetStaticPaths, GetStaticProps, NextPage } from "next";
import Head from "next/head";
import Image from "next/image";
import { StructuredText, StructuredTextGraphQlResponse } from "react-datocms";
import { nodeRules, request } from "utilities";

export type PersonPageProps = PersonQuery;

export const getStaticPaths: GetStaticPaths = async () => {
  const result = await request(PersonSlugsDocument, {}, {});

  return {
    paths: result.allPeople.map((person) => `/people/${person.slug}`),
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps<
  PersonPageProps,
  { slug: string }
> = async ({ params, preview }) => {
  if (!params) {
    throw "No parameters found.";
  }

  const result = await request(
    PersonDocument,
    { slug: params.slug },
    { includeDrafts: preview }
  );

  return {
    props: { ...result },
  };
};

const PersonPage: NextPage<PersonPageProps> = ({
  navigation,
  footer,
  person,
}) => {
  if (!person || !navigation || !footer) {
    throw "Article, navigation, or footer data not found.";
  }

  return (
    <>
      <Head>
        <title>{`${person.name} - JCore`}</title>
        <meta name="description" content={person.name} />
        <meta property="og:image" content={person.image.url} />
      </Head>
      <Navigation {...navigation} />
      <Container paddingY={{ base: 10, md: 14 }}>
        <Image
          width={1000}
          height={1000}
          src={person.image.url}
          alt={person.image.alt}
          title={person.image.title}
        />
      </Container>
      <Container paddingBottom={{ base: 10, md: 14 }} maxWidth="container.md">
        <Stack spacing={10}>
          <Stack spacing={2}>
            <Heading>{person.name}</Heading>
          </Stack>
          <Stack spacing={5}>
            <StructuredText
              customNodeRules={nodeRules}
              data={person.content.value as StructuredTextGraphQlResponse}
            />
          </Stack>
        </Stack>
      </Container>
      <Footer {...footer} />
    </>
  );
};

export default PersonPage;
